import type { FocusEvent, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CollapsibleSection,
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  BarChart3,
  FileBarChart,
  HelpCircle,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { selectIsRogerEmrEnabled } from '../store/slices/authSlice';
import { FeatureFlag, PermissionSlug } from '../constants/featureFlags';
import {
  deleteReportDefinition,
  getReportingManifest,
  listReportDefinitions,
  updateReportDefinition,
} from '../services/reportingApi';
import type { ReportListItem, ReportingManifest } from '../types/reporting';
import { formatDateInPT } from '../utils/dateUtils';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import SaveReportDialog from '../components/reporting/SaveReportDialog';
import { reportCatalog } from '../reporting/reportCatalog';

// One compact baseline height for every report card so description-less cards
// (which reserve an empty description slot) align with two-line cards across
// standard and saved report grids.
const CARD_CLASS =
  'h-full min-h-[5.5rem] cursor-pointer p-3 sm:p-3 md:p-3 transition-colors hover:bg-accent/50';

const GRID_CLASS =
  'grid auto-rows-fr grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3';

interface GroupedCard {
  key: string;
  groupLabel: string;
  node: ReactNode;
}

function groupCards(cards: GroupedCard[]): Array<{
  label: string;
  cards: GroupedCard[];
}> {
  const groups: Array<{ label: string; cards: GroupedCard[] }> = [];
  for (const card of cards) {
    let group = groups.find(candidate => candidate.label === card.groupLabel);
    if (!group) {
      group = { label: card.groupLabel, cards: [] };
      groups.push(group);
    }
    group.cards.push(card);
  }
  return groups;
}

type CardActionState =
  | { kind: 'edit'; report: ReportListItem }
  | { kind: 'delete'; report: ReportListItem }
  | { kind: 'share'; report: ReportListItem };

type PendingFocus =
  { kind: 'link'; id: string } | { kind: 'heading'; id: string };

// A ⋯ DropdownMenu, wrapped in a Tooltip for icon-only discoverability. Rendered
// only for cards `canManage` allows (own agency reports), so it never appears on
// system or teammates' cards.
function ReportCardMenu({
  report,
  mutating,
  onEdit,
  onMakePrivate,
  onShare,
  onDelete,
}: {
  report: ReportListItem;
  mutating: boolean;
  onEdit: () => void;
  onMakePrivate: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu modal={false}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='relative z-10 h-8 w-8 shrink-0 p-0'
                aria-label={`Actions for ${report.name}`}
                disabled={mutating}
              >
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side='bottom'>Report actions</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className='mr-2 h-4 w-4' />
            Edit details
          </DropdownMenuItem>
          {/* D9 skew guard: only offer a visibility change when the row
              carries an explicit value — a list served by an old backend
              has no such field, so these items simply don't appear. */}
          {report.visibility === 'agency' && (
            <DropdownMenuItem onClick={onMakePrivate}>
              <Lock className='mr-2 h-4 w-4' />
              Make private
            </DropdownMenuItem>
          )}
          {report.visibility === 'private' && (
            <DropdownMenuItem onClick={onShare}>
              <Users className='mr-2 h-4 w-4' />
              Share with agency
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className='text-destructive'>
            <Trash2 className='mr-2 h-4 w-4' />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

function ReportDescription({
  description,
  linkTo,
}: {
  description: string | null;
  linkTo?: string;
}) {
  const text = description ?? '';
  const className = 'min-h-[2rem] text-xs text-muted-foreground line-clamp-2';

  if (!text) {
    return <p className={className} />;
  }

  const triggerClassName = `${className} relative z-10 block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;
  const trigger = linkTo ? (
    <Link to={linkTo} className={triggerClassName}>
      {text}
    </Link>
  ) : (
    <p tabIndex={0} className={triggerClassName}>
      {text}
    </p>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent className='max-w-xs'>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Shelf heading + a hover-triggered info popover explaining what the shelf
// means, built on the existing controlled `Popover` — no Radix HoverCard
// primitive exists in the design system (see design §5). Popover (not
// Tooltip) so the explanation stays reachable on touch — see
// EligibilityCheckStep.tsx / AiExtractedInsuranceSuggestion.tsx for the same
// choice of primitive. Timer/state hygiene (clearCloseTimeout, onOpenChange
// resetting pending timers on Radix-initiated closes) follows
// LongTextPreview.tsx, but NOT its click-pin state machine — this popover
// holds one static sentence with nothing tabbable inside it, so "pinned" and
// "open" collapse to the same state and that machinery would be dead weight.
//
// Hover-capable devices (mouse + fine pointer, checked once per mount via
// matchMedia) open on mouseenter/mouseleave with a 150ms close grace — long
// enough to cross the 4px sideOffset gap from trigger into the panel without
// flickering shut — and a click is intercepted with preventDefault (which
// composeEventHandlers/Slot honor) so it can only OPEN, never toggle closed;
// otherwise a hover-then-click sequence would vanish the panel. Coarse
// pointers (touch) get zero mouse handlers and zero click override: the tap
// path is Radix's untouched native open/close toggle, byte-for-byte today's
// shipped behavior. All devices open on genuine keyboard focus — gated on
// `:focus-visible` so Tab opens it but tap-focus doesn't reintroduce an
// open-then-immediately-retoggled-shut flash on touch — and close on blur or
// Escape (Escape/outside-click dismissal remains Radix's own). `id` +
// `tabIndex={-1}` stay on the <h2> itself: the post-mutation
// focus-restoration effect above focuses headings by id.
function ShelfHeading({
  id,
  label,
  infoLabel,
  children,
}: {
  id: string;
  label: string;
  infoLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Evaluated once per mount, not on every render — a device's hover/pointer
  // capability doesn't change mid-session.
  const [hoverCapable] = useState(
    () => window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout]);

  const closeSoon = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 150);
  }, [clearCloseTimeout]);

  // Mirrors Radix-initiated changes (outside click, Escape) into our state
  // and clears any pending close timer so it can't later fire against a
  // popover Radix already closed.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) clearCloseTimeout();
    },
    [clearCloseTimeout]
  );

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const hoverHandlers = hoverCapable
    ? { onMouseEnter: openNow, onMouseLeave: closeSoon }
    : {};

  return (
    <div className='flex items-center gap-1'>
      <h2
        id={id}
        tabIndex={-1}
        className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
      >
        {label}
      </h2>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type='button'
            aria-label={infoLabel}
            className='rounded p-1.5 -m-1 text-muted-foreground transition-colors hover:text-foreground cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            onFocus={(e: FocusEvent<HTMLButtonElement>) => {
              if (e.currentTarget.matches(':focus-visible')) openNow();
            }}
            onBlur={closeSoon}
            {...(hoverCapable
              ? {
                  onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    // Suppress Radix's native click-toggle (composeEventHandlers
                    // checks defaultPrevented) so click is open-only here —
                    // hover already owns opening/closing on this device.
                    e.preventDefault();
                    openNow();
                  },
                }
              : {})}
            {...hoverHandlers}
          >
            <HelpCircle className='h-3.5 w-3.5' aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side='bottom'
          align='start'
          className='w-72 text-xs'
          aria-labelledby={id}
          onOpenAutoFocus={e => e.preventDefault()}
          {...hoverHandlers}
        >
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const ReportsLanding = () => {
  const navigate = useNavigate();
  const {
    agencyClinics,
    agencyClinicsLoading,
    agencyClinicsError,
    fetchAgencyClinics,
    hasReportsAccess,
    hasRogerEmrReportsAccess,
    hasPatientRosterAccess,
    hasWorklogView,
    hasBillingAccess,
  } = useStore(
    useShallow(state => ({
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      agencyClinicsError: state.agencyClinicsError,
      fetchAgencyClinics: state.fetchAgencyClinics,
      hasReportsAccess: state.hasPermission(PermissionSlug.REPORTS_ACCESS),
      hasRogerEmrReportsAccess: state.hasPermission(
        PermissionSlug.ROGER_EMR_REPORTS_ACCESS
      ),
      hasPatientRosterAccess:
        state.hasPermission(PermissionSlug.REPORTS_PATIENTS) &&
        state.hasPermission(PermissionSlug.REPORTS_EPISODES),
      hasWorklogView: state.hasPermission(PermissionSlug.WORKLOG_VIEW),
      hasBillingAccess: state.hasPermission(PermissionSlug.BILLING_ACCESS),
    }))
  );
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const user = useStore(state => state.user);
  // Product access is an explicit live selector. Flags and dataset permissions
  // cannot grant advanced reporting to a Docs-only agency.
  const advancedCatalogVisible = useStore(selectIsRogerEmrEnabled);
  const hasAdvancedDatasetPermission = useStore(state =>
    state.hasAnyPermission([
      PermissionSlug.REPORTS_VISITS,
      PermissionSlug.REPORTS_EPISODES,
      PermissionSlug.REPORTS_PATIENTS,
      PermissionSlug.REPORTS_CLAIMS,
      PermissionSlug.REPORTS_USER_LICENSES,
    ])
  );
  // Subscribe to the DERIVED boolean, not the stable hasFeatureFlag fn: the fn
  // ref never changes when feature_flags change, so subscribing to it alone
  // leaves the page unable to re-render on a mid-session flag flip (a grant that
  // should reveal Reports, or a revoke that should hide it). The boolean flips,
  // so the component re-renders and the load effect (keyed on flagEnabled) runs.
  const flagEnabled = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.ADVANCED_REPORTING, 'workos')
  );
  const advancedEnabled =
    advancedCatalogVisible && flagEnabled && hasAdvancedDatasetPermission;
  const [manifest, setManifest] = useState<ReportingManifest | null>(null);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(advancedEnabled);
  // A fetch failure is distinct from a permissions/flag denial: without this an
  // API error left `manifest` null and the page fell through to the "no access"
  // copy, telling a permitted user they lack access. Tracked separately so the
  // error branch (with Retry) renders instead.
  const [error, setError] = useState(false);
  // Bumped by Retry to re-run the load effect.
  const [reloadKey, setReloadKey] = useState(0);
  const hasRequestedAgencyClinicsRef = useRef(false);

  // At most one request per report is ever outstanding (D6): every mutation
  // handler early-returns while its report's id is present here, adds it before
  // the request, and clears it in `finally`. The card's ⋯ trigger disables (and
  // the share/delete dialogs' `loading` binds) on the same set.
  const [mutatingIds, setMutatingIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const beginMutation = (id: string) =>
    setMutatingIds(prev => new Set(prev).add(id));
  const endMutation = (id: string) =>
    setMutatingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // One page-level dialog instance per kind, fed by `cardAction.report` — never
  // one dialog per card.
  const [cardAction, setCardAction] = useState<CardActionState | null>(null);

  // Focus restoration (§2.8, must-fix 5): Radix's own close-focus attempt would
  // land on <body> once the focused element's card moves between sections or is
  // removed. Set in the same tick as the local splice/removal; the effect below
  // runs after Radix's attempt and moves focus explicitly.
  const [pendingFocus, setPendingFocus] = useState<PendingFocus | null>(null);
  useEffect(() => {
    if (!pendingFocus) return;
    const raf = requestAnimationFrame(() => {
      const targetId =
        pendingFocus.kind === 'link'
          ? `report-link-${pendingFocus.id}`
          : pendingFocus.id;
      document.getElementById(targetId)?.focus();
      // Clear only after focusing — clearing synchronously here in the effect
      // body (before the rAF fires) would trigger a re-render whose cleanup
      // cancels this very rAF before it runs.
      setPendingFocus(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingFocus]);

  useEffect(() => {
    if (
      !advancedCatalogVisible ||
      !hasRogerEmrReportsAccess ||
      hasRequestedAgencyClinicsRef.current ||
      agencyClinics.length > 0 ||
      agencyClinicsLoading
    ) {
      return;
    }
    // Keep initialization one-shot. A failed request renders an explicit
    // retry action instead of looping from this effect.
    hasRequestedAgencyClinicsRef.current = true;
    void fetchAgencyClinics();
  }, [
    agencyClinics.length,
    agencyClinicsLoading,
    fetchAgencyClinics,
    hasRogerEmrReportsAccess,
    advancedCatalogVisible,
  ]);

  const retryAgencyClinics = useCallback(() => {
    void fetchAgencyClinics(undefined, { force: true });
  }, [fetchAgencyClinics]);

  useEffect(() => {
    let cancelled = false;
    if (!advancedEnabled) {
      setManifest(null);
      setReports([]);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    Promise.all([getReportingManifest(), listReportDefinitions()])
      .then(([manifestData, reportList]) => {
        if (cancelled) return;
        setManifest(manifestData);
        setReports(reportList);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load reports', error);
        setError(true);
        showError(
          'Reports',
          'Unable to load custom reports. Please try again.'
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [showError, reloadKey, advancedEnabled]);

  const retry = useCallback(() => setReloadKey(key => key + 1), []);

  const datasetLabels = useMemo(
    () =>
      new Map(
        (manifest?.datasets ?? []).map(dataset => [dataset.key, dataset.label])
      ),
    [manifest]
  );

  // Datasets that live under a permission umbrella (falls/infections under
  // episodes) are badged with the umbrella's label, so the home page groups
  // them the way they are authorized. The builder's picker still shows each
  // dataset's own label.
  const datasetUmbrellas = useMemo(
    () =>
      new Map(
        (manifest?.datasets ?? []).map(dataset => [
          dataset.key,
          dataset.umbrella_key ?? dataset.key,
        ])
      ),
    [manifest]
  );

  // Normalize the search term once and use it for both filtering and the
  // "searching" decision, so whitespace-only or padded input can't disagree
  // (e.g. "   " filtering everything out while still counting as not-searching,
  // or " Friday " missing "Friday productivity").
  const needle = search.trim().toLowerCase();
  const matchesText = (...fields: (string | null | undefined)[]) =>
    fields.some(f => (f ?? '').toLowerCase().includes(needle));
  const matches = (report: ReportListItem) =>
    matchesText(
      report.name,
      report.description,
      // Also search the dataset label ("Visits") and the owner name so a report
      // is findable by what it's about and who saved it, not just its title.
      datasetLabels.get(report.dataset_key),
      report.created_by_name
    );

  const byName = (a: ReportListItem, b: ReportListItem) =>
    a.name.localeCompare(b.name);

  const systemReports = advancedEnabled
    ? reports.filter(
        r =>
          r.source === 'system' &&
          r.system_key !== 'patient-roster' &&
          r.system_key !== 'pcr' &&
          matches(r)
      )
    : [];
  // Missing/null visibility lands in the shared section — the read-direction
  // deploy-skew guard (D9): a list served by an old backend has no such field,
  // and today's behavior is "everyone can see it".
  const agencyReports = reports
    .filter(r => advancedCatalogVisible && r.source === 'agency' && matches(r))
    .sort(byName);
  const hasReportsPatients = useStore(state =>
    state.hasPermission(PermissionSlug.REPORTS_PATIENTS)
  );
  const hasReportsEpisodes = useStore(state =>
    state.hasPermission(PermissionSlug.REPORTS_EPISODES)
  );
  const canUseAdvancedPatientRoster = advancedEnabled && hasPatientRosterAccess;
  const canUseAdvancedPcr = Boolean(
    advancedEnabled &&
    hasReportsEpisodes &&
    hasReportsPatients &&
    hasRogerEmrReportsAccess
  );
  const widgetOnlyReports = agencyReports.filter(r => r.widget_only);
  const listedReports = agencyReports.filter(r => !r.widget_only);
  const privateReports = listedReports.filter(r => r.visibility === 'private');
  const sharedReports = listedReports.filter(r => r.visibility !== 'private');
  const standardReports = reportCatalog
    .map(report => ({
      ...report,
      href:
        report.slug === 'patient-roster'
          ? canUseAdvancedPatientRoster
            ? '/reports/builder?system=patient-roster'
            : '/reports/patient-roster'
          : report.slug === 'pcr'
            ? canUseAdvancedPcr
              ? '/reports/builder?system=pcr'
              : '/reports/pcr'
            : report.modernPath,
      icon:
        report.icon === 'bar-chart' ? (
          <BarChart3 className='h-4 w-4 shrink-0 text-muted-foreground' />
        ) : (
          <FileBarChart className='h-4 w-4 shrink-0 text-muted-foreground' />
        ),
      // Keep report-specific access rules at this page boundary. The catalog
      // describes cards and routes; it must not become an authorization table.
      visible:
        report.slug === 'adoption'
          ? hasReportsAccess
          : report.slug === 'patient-roster'
            ? canUseAdvancedPatientRoster || hasRogerEmrReportsAccess
            : report.slug === 'pcr'
              ? hasRogerEmrReportsAccess &&
                (agencyClinics ?? []).some(clinic => clinic.using_rcd)
              : report.slug === 'worklog'
                ? hasRogerEmrReportsAccess && hasWorklogView
                : report.slug === 'ar-roll-forward'
                  ? hasBillingAccess
                  : hasRogerEmrReportsAccess,
    }))
    .filter(
      report =>
        report.visible &&
        (advancedCatalogVisible || report.badge !== 'EMR') &&
        matchesText(report.name, report.description)
    );

  // Hide a section's header (not just its cards) when a search empties it, so a
  // heading never floats over nothing; a search that empties all sections shows
  // one combined empty state instead.
  const searching = needle !== '';
  const standardVisible =
    systemReports.length > 0 || standardReports.length > 0;
  const privateVisible = privateReports.length > 0;
  const clinicMetadataUnavailable =
    advancedCatalogVisible &&
    hasRogerEmrReportsAccess &&
    agencyClinics.length === 0 &&
    !agencyClinicsLoading &&
    Boolean(agencyClinicsError);
  const sharedVisible = sharedReports.length > 0;
  const widgetOnlyVisible = widgetOnlyReports.length > 0;

  const reportHref = (report: ReportListItem) =>
    report.source === 'system'
      ? `/reports/builder?system=${report.system_key}`
      : `/reports/builder?id=${report.id}`;

  // A row can be managed (edited, its visibility changed, deleted) only by the
  // agency-report it belongs to and only by the user who created it.
  const canManage = (r: ReportListItem) =>
    r.source === 'agency' &&
    r.id != null &&
    r.created_by != null &&
    r.created_by === user?.id;

  const metaLine = (report: ReportListItem): string | null => {
    if (report.source !== 'agency' || !report.created_at) return null;
    const date = formatDateInPT(report.created_at);
    // Private rows are always yours — no byline needed.
    if (report.visibility === 'private') return `Saved ${date}`;
    if (canManage(report)) return `Saved by you · ${date}`;
    if (report.created_by_name)
      return `Saved by ${report.created_by_name} · ${date}`;
    return null;
  };

  const applyUpdated = (updated: ReportListItem) =>
    setReports(prev => prev.map(r => (r.id === updated.id ? updated : r)));
  const removeReport = (id: string) =>
    setReports(prev => prev.filter(r => r.id !== id));

  const handleEditDetails = async (
    report: ReportListItem,
    name: string,
    description: string
  ): Promise<void> => {
    const id = report.id;
    if (!id || mutatingIds.has(id)) return;
    beginMutation(id);
    try {
      const updated = await updateReportDefinition(id, { name, description });
      applyUpdated(updated);
      showSuccess('Reports', 'Report updated.');
    } catch (e) {
      console.error('Failed to update report', e);
      showError(
        'Reports',
        getBusinessErrorMessage(e) ?? 'Failed to update report.'
      );
      // Re-throw so SaveReportDialog (which only closes after onSave resolves)
      // stays open on failure.
      throw e;
    } finally {
      endMutation(id);
    }
  };

  const handleMakePrivate = async (report: ReportListItem): Promise<void> => {
    const id = report.id;
    if (!id || mutatingIds.has(id)) return;
    beginMutation(id);
    try {
      const updated = await updateReportDefinition(id, {
        visibility: 'private',
      });
      // D9 skew guard: an old backend silently ignores an unknown `visibility`
      // field and returns 200 as if nothing changed — treat that as a failure
      // rather than lying to the user with a success toast.
      if (updated.visibility !== 'private') {
        console.error(
          'Report visibility update did not take effect',
          updated.visibility
        );
        showError('Reports', 'Failed to update report sharing.');
        return;
      }
      applyUpdated(updated);
      showSuccess('Reports', 'Report is now private.');
      setPendingFocus({ kind: 'link', id });
    } catch (e) {
      console.error('Failed to update report sharing', e);
      showError(
        'Reports',
        getBusinessErrorMessage(e) ?? 'Failed to update report sharing.'
      );
    } finally {
      endMutation(id);
    }
  };

  const handleShare = async (report: ReportListItem): Promise<void> => {
    const id = report.id;
    if (!id || mutatingIds.has(id)) return;
    beginMutation(id);
    try {
      const updated = await updateReportDefinition(id, {
        visibility: 'agency',
      });
      if (updated.visibility !== 'agency') {
        console.error(
          'Report visibility update did not take effect',
          updated.visibility
        );
        showError('Reports', 'Failed to update report sharing.');
        return;
      }
      applyUpdated(updated);
      showSuccess('Reports', 'Report shared with your agency.');
      // Close only on verified success — stays open on error/mismatch.
      setCardAction(null);
      setPendingFocus({ kind: 'link', id });
    } catch (e) {
      console.error('Failed to update report sharing', e);
      showError(
        'Reports',
        getBusinessErrorMessage(e) ?? 'Failed to update report sharing.'
      );
    } finally {
      endMutation(id);
    }
  };

  const handleDelete = async (report: ReportListItem): Promise<void> => {
    const id = report.id;
    if (!id || mutatingIds.has(id)) return;
    beginMutation(id);
    const headingId =
      report.visibility === 'private'
        ? 'reports-private-heading'
        : 'reports-shared-heading';
    try {
      await deleteReportDefinition(id);
      removeReport(id);
      showSuccess('Reports', 'Report deleted.');
      setCardAction(null);
      setPendingFocus({ kind: 'heading', id: headingId });
    } catch (e) {
      console.error('Failed to delete report', e);
      showError(
        'Reports',
        getBusinessErrorMessage(e) ?? 'Failed to delete report.'
      );
    } finally {
      endMutation(id);
    }
  };

  // Real links: keyboard focus + Enter activation + middle-click new tab. The
  // ⋯ menu (when present) is a SIBLING of the anchor, not nested inside it — a
  // button inside an anchor is invalid HTML — via the stretched-link pattern:
  // the anchor gets an `after:absolute after:inset-0` overlay that makes the
  // whole card clickable while the menu trigger sits above it (`relative z-10`).
  const reportCard = (report: ReportListItem) => {
    const meta = metaLine(report);
    const mutating = report.id ? mutatingIds.has(report.id) : false;
    return (
      <Card
        key={report.system_key ?? report.id}
        className='relative h-full min-h-[5.5rem] p-3 sm:p-3 md:p-3 transition-colors hover:bg-accent/50'
      >
        <CardContent className='flex h-full flex-col justify-between gap-2 p-0'>
          <div className='space-y-1'>
            <div className='flex items-start justify-between gap-2'>
              <Link
                to={reportHref(report)}
                id={`report-link-${report.id ?? report.system_key}`}
                className='flex items-center gap-2 text-sm font-medium focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-ring'
              >
                <FileBarChart className='h-4 w-4 shrink-0 text-muted-foreground' />
                {report.name}
              </Link>
              {canManage(report) && (
                <ReportCardMenu
                  report={report}
                  mutating={mutating}
                  onEdit={() => setCardAction({ kind: 'edit', report })}
                  onMakePrivate={() => handleMakePrivate(report)}
                  onShare={() => setCardAction({ kind: 'share', report })}
                  onDelete={() => setCardAction({ kind: 'delete', report })}
                />
              )}
            </div>
            <ReportDescription
              description={report.description}
              linkTo={reportHref(report)}
            />
          </div>
          {meta && <p className='text-xs text-muted-foreground'>{meta}</p>}
        </CardContent>
      </Card>
    );
  };

  // Genuine access denial: no fixed reports and, after a successful advanced
  // load, no accessible datasets. A fetch error is handled separately below so
  // it can't masquerade as a permissions problem.
  if (
    standardReports.length === 0 &&
    (!advancedEnabled ||
      (!loading && !error && (manifest?.datasets.length ?? 0) === 0))
  ) {
    return (
      <div>
        <div className='rounded-lg border border-dashed border-border p-8 text-center'>
          <p className='text-sm font-medium text-foreground'>
            No reports available
          </p>
          <p className='mt-1 text-sm text-muted-foreground'>
            Ask your administrator if you need a report added to your account.
          </p>
        </div>
      </div>
    );
  }

  const activeDeleteId =
    cardAction?.kind === 'delete' ? cardAction.report.id : null;
  const activeShareId =
    cardAction?.kind === 'share' ? cardAction.report.id : null;

  const standardCards: GroupedCard[] = [
    ...systemReports.map(report => ({
      key: report.system_key ?? report.id ?? report.name,
      groupLabel:
        datasetLabels.get(
          datasetUmbrellas.get(report.dataset_key) ?? report.dataset_key
        ) ?? report.dataset_key,
      node: reportCard(report),
    })),
    ...standardReports.map(report => ({
      key: report.href,
      groupLabel: report.badge,
      node: (
        <Card key={report.href} className={`relative ${CARD_CLASS}`}>
          <CardContent className='flex h-full flex-col gap-2 p-0'>
            <div className='space-y-1'>
              <Link
                to={report.href}
                className='flex items-center gap-2 text-sm font-medium focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-ring'
              >
                {report.icon}
                {report.name}
              </Link>
              <ReportDescription
                description={report.description}
                linkTo={report.href}
              />
            </div>
          </CardContent>
        </Card>
      ),
    })),
  ];

  const renderGroupedCards = (cards: GroupedCard[]) =>
    groupCards(cards).map(group => (
      <section key={group.label} className='space-y-2'>
        <h3 className='text-sm font-medium text-foreground'>{group.label}</h3>
        <div className={GRID_CLASS}>{group.cards.map(card => card.node)}</div>
      </section>
    ));

  const standardReportsSection = (
    <section className='space-y-3'>
      <h2 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        Standard reports
      </h2>
      <div className='space-y-4'>{renderGroupedCards(standardCards)}</div>
    </section>
  );

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
        <div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center'>
          {advancedEnabled && (
            <Button
              className='w-full sm:w-auto'
              onClick={() => navigate('/reports/builder')}
            >
              <Plus className='h-4 w-4' />
              New report
            </Button>
          )}
          <div className='relative w-full sm:w-64'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              inputSize='default'
              className='pl-8 w-full'
              placeholder='Search reports…'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {clinicMetadataUnavailable && (
        <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-8 text-center'>
          <AlertTriangle
            className='h-8 w-8 text-muted-foreground/60'
            aria-hidden
          />
          <p className='text-sm text-muted-foreground'>
            Unable to load clinic information. PCR Report is hidden until it
            loads successfully.
          </p>
          <Button variant='outline' size='sm' onClick={retryAgencyClinics}>
            Retry clinic information
          </Button>
        </div>
      )}

      {loading ? (
        // Card skeletons while loading — never blank report sections that read
        // as "no reports".
        <section className='space-y-3'>
          <Skeleton className='h-4 w-32' />
          <div className={GRID_CLASS}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className='h-[5.5rem] w-full rounded-lg' />
            ))}
          </div>
        </section>
      ) : error ? (
        <>
          {standardVisible && standardReportsSection}
          {/* Advanced report loading is independent of the fixed report cards.
              Keep permission-derived reports reachable when the builder API is
              temporarily unavailable or an older deployment lacks it. */}
          <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-8 text-center'>
            <AlertTriangle
              className='h-8 w-8 text-muted-foreground/60'
              aria-hidden
            />
            <p className='text-sm text-muted-foreground'>
              Unable to load custom reports. Standard reports are still
              available.
            </p>
            <Button variant='outline' size='sm' onClick={retry}>
              Retry
            </Button>
          </div>
        </>
      ) : searching &&
        !standardVisible &&
        !privateVisible &&
        !sharedVisible &&
        !widgetOnlyVisible ? (
        <p className='text-sm text-muted-foreground'>
          No reports match &ldquo;{search}&rdquo;.{' '}
          <button
            type='button'
            className='underline hover:text-foreground'
            onClick={() => setSearch('')}
          >
            Clear search
          </button>
        </p>
      ) : (
        <>
          {(standardVisible || !searching) && standardReportsSection}

          {/* Always rendered when not searching (D7) — the private shelf is the
              headline deliverable, and a section that only appears after the
              feature has already been discovered would hide the concept from
              exactly the users who need to learn it. */}
          {advancedEnabled && (privateVisible || !searching) && (
            <section className='space-y-3'>
              <ShelfHeading
                id='reports-private-heading'
                label='Your private reports'
                infoLabel='About your private reports'
              >
                Only you can open these.
              </ShelfHeading>
              {privateVisible ? (
                <div className='space-y-4'>
                  {renderGroupedCards(
                    privateReports.map(report => ({
                      key: report.id ?? report.name,
                      groupLabel:
                        datasetLabels.get(
                          datasetUmbrellas.get(report.dataset_key) ??
                            report.dataset_key
                        ) ?? report.dataset_key,
                      node: reportCard(report),
                    }))
                  )}
                </div>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  No private reports yet. Choose "Only me" when you save a
                  report.
                </p>
              )}
            </section>
          )}

          {advancedEnabled && (sharedVisible || !searching) && (
            <section className='space-y-3'>
              <ShelfHeading
                id='reports-shared-heading'
                label='Shared with your agency'
                infoLabel='About shared reports'
              >
                Visible to coworkers with access to this data.
              </ShelfHeading>
              {sharedVisible ? (
                <div className='space-y-4'>
                  {renderGroupedCards(
                    sharedReports.map(report => ({
                      key: report.id ?? report.system_key ?? report.name,
                      groupLabel:
                        datasetLabels.get(
                          datasetUmbrellas.get(report.dataset_key) ??
                            report.dataset_key
                        ) ?? report.dataset_key,
                      node: reportCard(report),
                    }))
                  )}
                </div>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  No saved reports yet. Open a standard report and "Save a
                  copy", or build one from scratch.
                </p>
              )}
            </section>
          )}

          {advancedEnabled && widgetOnlyVisible && (
            <CollapsibleSection
              title={`Widget-only reports (${widgetOnlyReports.length})`}
              defaultOpen={false}
            >
              <div className={GRID_CLASS}>
                {widgetOnlyReports.map(reportCard)}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}

      <SaveReportDialog
        open={cardAction?.kind === 'edit'}
        onOpenChange={open => !open && setCardAction(null)}
        mode='edit'
        initialName={cardAction?.report.name ?? ''}
        initialDescription={cardAction?.report.description ?? ''}
        onSave={async (name, description) => {
          if (cardAction?.kind === 'edit') {
            await handleEditDetails(cardAction.report, name, description);
          }
        }}
      />

      <ConfirmationDialog
        open={cardAction?.kind === 'delete'}
        title={
          cardAction?.kind === 'delete'
            ? `Delete "${cardAction.report.name}"?`
            : ''
        }
        message='This permanently deletes the report for everyone who can see it.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        loading={activeDeleteId != null && mutatingIds.has(activeDeleteId)}
        onAccept={() => {
          if (cardAction?.kind === 'delete') handleDelete(cardAction.report);
        }}
        onCancel={() => setCardAction(null)}
      />

      <ConfirmationDialog
        open={cardAction?.kind === 'share'}
        title={
          cardAction?.kind === 'share'
            ? `Share "${cardAction.report.name}" with your agency?`
            : ''
        }
        message='People in your agency with access to this report type will be able to open it, including its saved filters.'
        confirmLabel='Share'
        cancelLabel='Cancel'
        loading={activeShareId != null && mutatingIds.has(activeShareId)}
        onAccept={() => {
          if (cardAction?.kind === 'share') handleShare(cardAction.report);
        }}
        onCancel={() => setCardAction(null)}
      />
    </div>
  );
};

export default ReportsLanding;
